// only available on chrome and edge, ~1000+fps at 1080p
/// <reference types="@webgpu/types" />

import { FrameParser } from "./types"

export class WebGPUFrameParser extends FrameParser {
    #device: GPUDevice | undefined
    #context: GPUCanvasContext
    #computePipeline: GPUComputePipeline | null = null
    #computeBindGroupLayout: GPUBindGroupLayout | null = null

    // NEW: Persistent Buffer to hold the "Raw Truth" of the entire video
    #histogramBuffer: GPUBuffer | null = null
    #histogramSize = 32768 * 4 // 32k u32 integers

    constructor(canvas: OffscreenCanvas) {
        super(canvas)
        this.#context = this.canvas.getContext("webgpu")!
    }

    async init() {
        if (!navigator.gpu) throw new Error("WebGPU is not supported.")

        const adapter = await navigator.gpu.requestAdapter()
        if (!adapter) throw new Error("Failed to get GPU adapter.")

        const deviceLimits = adapter.limits
        this.#device = await adapter.requestDevice({
            requiredLimits: {
                maxTextureDimension2D: Math.min(deviceLimits.maxTextureDimension2D, 16384),
            },
        })

        const format = navigator.gpu.getPreferredCanvasFormat()
        this.#context.configure({
            device: this.#device,
            format: format,
            alphaMode: "premultiplied",
        })

        // Initialize the persistent histogram buffer (Zeroed by default)
        this.#histogramBuffer = this.#device.createBuffer({
            size: this.#histogramSize,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
        })

        this.initPipeline()
    }

    initPipeline() {
        const device = this.#device!
        const computeShaderCode = `
            struct Params {
                width: u32,
                height: u32,
            }
 
            @group(0) @binding(0) var imgTexture: texture_2d<f32>;
            // Buffer 1: Timeline Index (Bitmask)
            @group(0) @binding(1) var<storage, read_write> colorspace: array<atomic<u32>>;
            @group(0) @binding(2) var<uniform> params: Params;
            // Buffer 2: Anomaly Histogram (Counts)
            @group(0) @binding(3) var<storage, read_write> pixelCounts: array<atomic<u32>>;

            // Helper: Convert RGB float to 15-bit ID
            fn get_color_id(color: vec4<f32>) -> u32 {
                let r = u32(color.r * 255.0) >> 3u;
                let g = u32(color.g * 255.0) >> 3u;
                let b = u32(color.b * 255.0) >> 3u;
                return r * 1024u + g * 32u + b;
            }
 
            @compute @workgroup_size(16, 16)
            fn main(@builtin(global_invocation_id) GlobalInvocationID: vec3<u32>) {
                let x = GlobalInvocationID.x;
                let y = GlobalInvocationID.y;
 
                if (x >= params.width || y >= params.height) {
                    return;
                }

                let color = textureLoad(imgTexture, vec2<i32>(i32(x), i32(y)), 0);
                let center_id = get_color_id(color);

                // 1. Timeline: Mark that this color exists in this frame (Bitmask)
                // "Raw Truth" - No filtering here.
                let byteIndex: u32 = center_id >> 5u;
                let bitMask: u32 = 1u << (center_id & 31u);
                atomicOr(&colorspace[byteIndex], bitMask);

                // 2. Anomaly: Increment the global count for this color
                // "Raw Truth" - We count EVERYTHING (including noise).
                // We will filter out the noise mathematically in 'finishAndGetAnomalyScores'.
                atomicAdd(&pixelCounts[center_id], 1u);
            }
        `
        const computeShaderModule = device.createShaderModule({ code: computeShaderCode })

        this.#computeBindGroupLayout = device.createBindGroupLayout({
            entries: [
                {
                    binding: 0,
                    visibility: GPUShaderStage.COMPUTE,
                    texture: { sampleType: "float" },
                },
                { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
                { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
                { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
            ],
        })

        this.#computePipeline = device.createComputePipeline({
            layout: device.createPipelineLayout({
                bindGroupLayouts: [this.#computeBindGroupLayout],
            }),
            compute: { module: computeShaderModule, entryPoint: "main" },
        })
    }

    async parseColorspace(frame: VideoFrame) {
        const device = this.#device!
        const width = frame.displayWidth
        const height = frame.displayHeight

        this.canvas.width = width
        this.canvas.height = height

        // 1. Setup per-frame resources
        const colorspaceSize = (32 * 32 * 32) / 8 // 4096 bytes
        const colorspaceBuffer = device.createBuffer({
            size: colorspaceSize,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
        })

        const paramsBuffer = device.createBuffer({
            size: 8,
            usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        })
        device.queue.writeBuffer(paramsBuffer, 0, new Uint32Array([width, height]))

        const videoTexture = device.createTexture({
            size: [width, height],
            format: "rgba8unorm",
            usage:
                GPUTextureUsage.TEXTURE_BINDING |
                GPUTextureUsage.COPY_DST |
                GPUTextureUsage.RENDER_ATTACHMENT,
        })
        device.queue.copyExternalImageToTexture({ source: frame }, { texture: videoTexture }, [
            width,
            height,
        ])

        // 2. Create Bind Group (Including the persistent histogram buffer)
        const computeBindGroup = device.createBindGroup({
            layout: this.#computeBindGroupLayout!,
            entries: [
                { binding: 0, resource: videoTexture.createView() },
                { binding: 1, resource: { buffer: colorspaceBuffer } },
                { binding: 2, resource: { buffer: paramsBuffer } },
                { binding: 3, resource: { buffer: this.#histogramBuffer! } }, // Persistent
            ],
        })

        // 3. Dispatch
        const commandEncoder = device.createCommandEncoder()
        const pass = commandEncoder.beginComputePass()
        pass.setPipeline(this.#computePipeline!)
        pass.setBindGroup(0, computeBindGroup)
        pass.dispatchWorkgroups(Math.ceil(width / 16), Math.ceil(height / 16))
        pass.end()

        // 4. Readback Bitmask (For Timeline)
        const readbackBuffer = device.createBuffer({
            size: colorspaceSize,
            usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
        })
        commandEncoder.copyBufferToBuffer(colorspaceBuffer, 0, readbackBuffer, 0, colorspaceSize)
        device.queue.submit([commandEncoder.finish()])

        await device.queue.onSubmittedWorkDone()

        await readbackBuffer.mapAsync(GPUMapMode.READ)
        const colorspace = new Uint32Array(readbackBuffer.getMappedRange().slice(0))
        readbackBuffer.unmap()

        // Cleanup per-frame resources
        readbackBuffer.destroy()
        paramsBuffer.destroy()
        videoTexture.destroy()
        colorspaceBuffer.destroy()

        return colorspace
    }

    /**
     * UNIVERSAL DENSITY SCORER
     * Theory: "Targets are Rare but Cohesive. Noise is Rare but Scattered."
     * 1. Cohesion Check: Blurs raw counts to punish isolated noise.
     * 2. Diffusion: Blurs the map again to find "Background Clouds" (Ocean).
     * 3. Isolation Score: Rare objects far from clouds get Score 0 (Anomaly).
     */
    async finishAndGetAnomalyScores(
        minPixelMass = 20, // Minimum "Cohesive Mass" to be real (Sparkle filter)
        isolationStrength = 3.0, // Tuning: Higher = anomalies must be more distinct
        diffusionPasses = 4 // How far does "Background" influence extend?
    ) {
        if (!this.#device || !this.#histogramBuffer) return null

        const size = this.#histogramSize
        const readbackBuffer = this.#device.createBuffer({
            size: size,
            usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
        })

        const commandEncoder = this.#device.createCommandEncoder()
        commandEncoder.copyBufferToBuffer(this.#histogramBuffer, 0, readbackBuffer, 0, size)
        this.#device.queue.submit([commandEncoder.finish()])

        await this.#device.queue.onSubmittedWorkDone()
        await readbackBuffer.mapAsync(GPUMapMode.READ)

        const rawCounts = new Uint32Array(readbackBuffer.getMappedRange().slice(0))
        readbackBuffer.unmap()
        readbackBuffer.destroy()

        const len = 32768

        // --- STEP 1: COHESION PASS (Noise Removal) ---
        // We blur the raw counts.
        // If 50 noise pixels are scattered, they average to ~1. (Fail)
        // If 50 target pixels are clustered, they average to ~50. (Pass)
        const cohesiveCounts = new Float32Array(len)

        for (let r = 0; r < 32; r++) {
            for (let g = 0; g < 32; g++) {
                for (let b = 0; b < 32; b++) {
                    let sum = 0
                    // 3x3x3 Box Blur
                    for (let dr = -1; dr <= 1; dr++) {
                        for (let dg = -1; dg <= 1; dg++) {
                            for (let db = -1; db <= 1; db++) {
                                const nr = r + dr
                                const ng = g + dg
                                const nb = b + db
                                if (
                                    nr >= 0 &&
                                    nr < 32 &&
                                    ng >= 0 &&
                                    ng < 32 &&
                                    nb >= 0 &&
                                    nb < 32
                                ) {
                                    sum += rawCounts[(nr << 10) | (ng << 5) | nb]
                                }
                            }
                        }
                    }
                    cohesiveCounts[(r << 10) | (g << 5) | b] = sum
                }
            }
        }

        // --- STEP 2: DENSITY FIELD (Background Definition) ---
        // We diffuse the cohesive counts to find "Global Background Clouds".
        let densityMap = new Float32Array(len)
        densityMap.set(cohesiveCounts)

        for (let pass = 0; pass < diffusionPasses; pass++) {
            const nextPass = new Float32Array(len)
            for (let i = 0; i < len; i++) {
                const r = i >> 10
                const g = (i >> 5) & 31
                const b = i & 31
                let sum = 0
                for (let dr = -1; dr <= 1; dr++) {
                    for (let dg = -1; dg <= 1; dg++) {
                        for (let db = -1; db <= 1; db++) {
                            const nr = r + dr
                            const ng = g + dg
                            const nb = b + db
                            if (nr >= 0 && nr < 32 && ng >= 0 && ng < 32 && nb >= 0 && nb < 32) {
                                sum += densityMap[(nr << 10) | (ng << 5) | nb]
                            }
                        }
                    }
                }
                nextPass[i] = sum / 27.0
            }
            densityMap = nextPass
        }

        let maxDensity = 1.0
        for (let i = 0; i < len; i++) {
            if (densityMap[i] > maxDensity) maxDensity = densityMap[i]
        }

        // --- STEP 3: SCORING ---
        const quantizedScores = new Uint8Array(len)
        const { pow, min, max } = Math

        // Threshold: A block of 'minPixelMass' pixels, summed over 27 neighbors.
        const massThreshold = minPixelMass * 27

        for (let i = 0; i < len; i++) {
            // GATE: Cohesion Check (Noise Killer)
            if (cohesiveCounts[i] < massThreshold) {
                quantizedScores[i] = 255
                continue
            }

            // ANALYSIS: Isolation
            // High Density = Close to Ocean. Low Density = Anomaly.
            const density = densityMap[i]
            const backgroundProb = density / maxDensity

            // SCORING: Power curve to highlight only the most isolated colors
            const safetyScore = pow(backgroundProb, 1 / isolationStrength)

            // Invert for Player: 0 = Anomaly (Orange), 255 = Safe (Blue)
            const finalByte = 255 - (1.0 - safetyScore) * 255

            quantizedScores[i] = min(255, max(0, finalByte))
        }

        return quantizedScores
    }
}
